import { useState, useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { GraftonChat } from "@/components/GraftonChat";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Plug, CheckCircle2, XCircle, Loader2, ArrowRight, Copy,
  ExternalLink, Eye, EyeOff, Zap, Shield, RefreshCw,
  Landmark, Calculator, Users, Mail, Share2, Bot, Brain,
  MessageSquare, FileText, Globe, UserPlus, Database,
  Link2, AlertCircle, Sparkles, ChevronDown, ChevronUp,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/* ─── Service definitions ─── */
interface ServiceDef {
  id: string;
  name: string;
  category: string;
  icon: React.ElementType;
  color: string;
  description: string;
  feeds: string[];
  setupSteps: string[];
  fields: { key: string; label: string; placeholder: string; secret?: boolean }[];
  docUrl?: string;
  logoUrl?: string;
}

const SERVICES: ServiceDef[] = [
  {
    id: "qonto",
    name: "Qonto",
    category: "Banque",
    icon: Landmark,
    color: "bg-violet-500/10 text-violet-500 border-violet-500/20",
    description: "Connectez votre compte bancaire Qonto pour suivre vos flux de trésorerie en temps réel.",
    feeds: ["Trésorerie", "Comptabilité", "KPIs financiers"],
    setupSteps: [
      "Connectez-vous à app.qonto.com",
      "Allez dans Paramètres > Intégrations > API",
      "Générez une clé API avec les droits 'Lecture'",
      "Copiez la clé et l'identifiant organisation",
    ],
    fields: [
      { key: "api_key", label: "Clé API", placeholder: "qonto_sk_...", secret: true },
      { key: "org_id", label: "ID Organisation", placeholder: "org_..." },
    ],
    docUrl: "https://api-doc.qonto.com/docs/business-api/7a2d7b3aa1b76-authentication",
  },
  {
    id: "pennylane",
    name: "Pennylane",
    category: "Comptabilité",
    icon: Calculator,
    color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    description: "Synchronisez votre comptabilité Pennylane : factures, TVA, bilans, comptes de résultat.",
    feeds: ["Comptabilité", "Bilan", "TVA", "KPIs financiers"],
    setupSteps: [
      "Connectez-vous à app.pennylane.com",
      "Allez dans Paramètres > API & Intégrations",
      "Créez un token API personnel",
      "Copiez le token et collez-le ci-dessous",
    ],
    fields: [
      { key: "api_token", label: "Token API", placeholder: "pl_token_...", secret: true },
      { key: "company_id", label: "ID Société (optionnel)", placeholder: "Automatique si un seul compte" },
    ],
    docUrl: "https://pennylane.readme.io/reference/getting-started",
  },
  {
    id: "axonaut",
    name: "Axonaut",
    category: "CRM",
    icon: Users,
    color: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    description: "Connectez votre CRM Axonaut pour synchroniser contacts, opportunités, devis et factures.",
    feeds: ["Ventes", "Pipeline", "Contacts", "Facturation"],
    setupSteps: [
      "Connectez-vous à axonaut.com",
      "Allez dans Mon compte > API",
      "Copiez votre clé API",
      "Collez-la ci-dessous",
    ],
    fields: [
      { key: "api_key", label: "Clé API Axonaut", placeholder: "ax_...", secret: true },
    ],
    docUrl: "https://axonaut.com/api",
  },
  {
    id: "gmail",
    name: "Gmail",
    category: "Email",
    icon: Mail,
    color: "bg-red-500/10 text-red-500 border-red-500/20",
    description: "Connectez graftonia1@gmail.com pour centraliser tous vos échanges email dans Grafton.",
    feeds: ["Emails", "Communication", "Suivi client"],
    setupSteps: [
      "Accédez à myaccount.google.com/apppasswords",
      "Sélectionnez 'Mail' et 'Autre (nom personnalisé)'",
      "Entrez 'Grafton IA' comme nom d'application",
      "Copiez le mot de passe d'application généré (16 caractères)",
    ],
    fields: [
      { key: "email", label: "Adresse Gmail", placeholder: "graftonia1@gmail.com" },
      { key: "app_password", label: "Mot de passe d'application", placeholder: "xxxx xxxx xxxx xxxx", secret: true },
    ],
    docUrl: "https://support.google.com/accounts/answer/185833",
  },
  {
    id: "linkedin",
    name: "LinkedIn",
    category: "Réseaux sociaux",
    icon: Share2,
    color: "bg-sky-500/10 text-sky-500 border-sky-500/20",
    description: "Publiez et analysez vos posts LinkedIn directement depuis Grafton.",
    feeds: ["Marketing", "Réseaux sociaux", "Notoriété"],
    setupSteps: [
      "Accédez à developer.linkedin.com",
      "Créez une application LinkedIn",
      "Demandez les permissions 'w_member_social' et 'r_liteprofile'",
      "Copiez le Client ID et Client Secret",
    ],
    fields: [
      { key: "client_id", label: "Client ID", placeholder: "78xxxx" },
      { key: "client_secret", label: "Client Secret", placeholder: "xxxxx", secret: true },
    ],
    docUrl: "https://learn.microsoft.com/en-us/linkedin/shared/authentication/authorization-code-flow",
  },
  {
    id: "instagram",
    name: "Instagram",
    category: "Réseaux sociaux",
    icon: Share2,
    color: "bg-pink-500/10 text-pink-500 border-pink-500/20",
    description: "Gérez votre présence Instagram : publications, stories, analytics.",
    feeds: ["Marketing", "Réseaux sociaux", "Engagement"],
    setupSteps: [
      "Accédez à developers.facebook.com",
      "Créez une application Meta Business",
      "Ajoutez le produit 'Instagram Graph API'",
      "Générez un token d'accès longue durée",
    ],
    fields: [
      { key: "access_token", label: "Token d'accès Instagram", placeholder: "IGQV...", secret: true },
      { key: "business_id", label: "Business Account ID", placeholder: "17841..." },
    ],
    docUrl: "https://developers.facebook.com/docs/instagram-api/getting-started",
  },
  {
    id: "facebook",
    name: "Facebook",
    category: "Réseaux sociaux",
    icon: Share2,
    color: "bg-indigo-500/10 text-indigo-500 border-indigo-500/20",
    description: "Gérez votre page Facebook : publications, publicités, analytics.",
    feeds: ["Marketing", "Réseaux sociaux", "Publicité"],
    setupSteps: [
      "Accédez à developers.facebook.com",
      "Utilisez la même application Meta que pour Instagram",
      "Ajoutez le produit 'Pages API'",
      "Copiez le Page Access Token",
    ],
    fields: [
      { key: "page_token", label: "Page Access Token", placeholder: "EAA...", secret: true },
      { key: "page_id", label: "Page ID", placeholder: "1234567890" },
    ],
    docUrl: "https://developers.facebook.com/docs/pages-api",
  },
  {
    id: "twitter",
    name: "X (Twitter)",
    category: "Réseaux sociaux",
    icon: Share2,
    color: "bg-slate-500/10 text-slate-400 border-slate-500/20",
    description: "Publiez et analysez vos tweets directement depuis Grafton.",
    feeds: ["Marketing", "Réseaux sociaux", "Veille"],
    setupSteps: [
      "Accédez à developer.twitter.com",
      "Créez un projet et une application",
      "Générez les clés API (Consumer + Bearer)",
      "Activez OAuth 2.0 avec les scopes tweet.read et tweet.write",
    ],
    fields: [
      { key: "api_key", label: "API Key", placeholder: "xxx" },
      { key: "api_secret", label: "API Secret", placeholder: "xxx", secret: true },
      { key: "bearer_token", label: "Bearer Token", placeholder: "AAAAAxx...", secret: true },
    ],
    docUrl: "https://developer.twitter.com/en/docs/authentication/oauth-2-0",
  },
  {
    id: "nanocorp",
    name: "NanoCorp",
    category: "Intelligence",
    icon: Bot,
    color: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20",
    description: "Connectez NanoCorp pour la prospection automatisée et l'intelligence marché.",
    feeds: ["Prospection", "Leads", "Intelligence marché"],
    setupSteps: [
      "Accédez à votre espace NanoCorp",
      "Allez dans Paramètres > Intégrations",
      "Générez un token d'intégration",
      "Collez-le ci-dessous",
    ],
    fields: [
      { key: "api_token", label: "Token NanoCorp", placeholder: "nc_...", secret: true },
    ],
  },
  {
    id: "grafton",
    name: "Grafton IA",
    category: "Intelligence",
    icon: Sparkles,
    color: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    description: "Votre associé IA principal. Orchestration, analyse, création et prospection.",
    feeds: ["Orchestration globale", "Tous les KPIs", "Décisions stratégiques"],
    setupSteps: [
      "Grafton IA est automatiquement connecté avec votre compte",
      "Aucune configuration supplémentaire requise",
    ],
    fields: [],
  },
  {
    id: "openai",
    name: "ChatGPT (OpenAI)",
    category: "Intelligence",
    icon: Brain,
    color: "bg-green-500/10 text-green-500 border-green-500/20",
    description: "Connectez ChatGPT pour les analyses avancées, la rédaction et la stratégie.",
    feeds: ["Analyse", "Rédaction", "Business Plan"],
    setupSteps: [
      "Accédez à platform.openai.com/api-keys",
      "Cliquez sur 'Create new secret key'",
      "Copiez la clé API générée",
      "Collez-la ci-dessous",
    ],
    fields: [
      { key: "api_key", label: "Clé API OpenAI", placeholder: "sk-...", secret: true },
    ],
    docUrl: "https://platform.openai.com/api-keys",
  },
  {
    id: "claude",
    name: "Claude (Anthropic)",
    category: "Intelligence",
    icon: Brain,
    color: "bg-orange-500/10 text-orange-500 border-orange-500/20",
    description: "Connectez Claude pour la rédaction juridique, l'analyse de documents et la conformité.",
    feeds: ["Rédaction", "Juridique", "Conformité"],
    setupSteps: [
      "Accédez à console.anthropic.com/settings/keys",
      "Cliquez sur 'Create Key'",
      "Copiez la clé API générée",
      "Collez-la ci-dessous",
    ],
    fields: [
      { key: "api_key", label: "Clé API Anthropic", placeholder: "sk-ant-...", secret: true },
    ],
    docUrl: "https://console.anthropic.com/settings/keys",
  },
  {
    id: "dgfip",
    name: "Impôts (DGFiP)",
    category: "Administration",
    icon: FileText,
    color: "bg-blue-800/10 text-blue-700 border-blue-800/20",
    description: "Connectez votre espace professionnel impots.gouv.fr pour le suivi fiscal automatisé.",
    feeds: ["Fiscalité", "TVA", "CFE", "IS"],
    setupSteps: [
      "Accédez à impots.gouv.fr > Espace professionnel",
      "Allez dans Paramètres du compte > Habilitations",
      "Notez votre numéro fiscal et SIREN",
      "Renseignez-les ci-dessous (Grafton ne stocke jamais vos identifiants)",
    ],
    fields: [
      { key: "siren", label: "SIREN", placeholder: "123 456 789" },
      { key: "numero_fiscal", label: "Numéro fiscal", placeholder: "12 34 567 890 123", secret: true },
    ],
    docUrl: "https://www.impots.gouv.fr/professionnel",
  },
  {
    id: "comptable",
    name: "Accès Comptable",
    category: "Administration",
    icon: UserPlus,
    color: "bg-teal-500/10 text-teal-500 border-teal-500/20",
    description: "Invitez votre expert-comptable pour qu'il accède aux données financières en lecture seule.",
    feeds: ["Comptabilité", "Bilan", "Audit"],
    setupSteps: [
      "Saisissez l'email de votre expert-comptable",
      "Choisissez le niveau d'accès (lecture seule ou complet)",
      "Il recevra une invitation par email",
      "Il pourra consulter les données sans modifier",
    ],
    fields: [
      { key: "email", label: "Email du comptable", placeholder: "cabinet@expert-comptable.fr" },
      { key: "access_level", label: "Niveau d'accès", placeholder: "Lecture seule" },
    ],
  },
  {
    id: "wix",
    name: "Wix",
    category: "Base de données",
    icon: Database,
    color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    description: "Connectez votre site Wix pour synchroniser les formulaires de contact, la base de données et les collections.",
    feeds: ["Contacts", "Formulaires", "CRM", "Base de données"],
    setupSteps: [
      "Accédez à dev.wix.com",
      "Créez une application OAuth ou utilisez une API Key",
      "Allez dans votre site Wix > Paramètres > API Keys",
      "Créez une clé avec les permissions 'WixData' et 'Contacts'",
      "Copiez l'Account ID et l'API Key",
    ],
    fields: [
      { key: "account_id", label: "Account ID Wix", placeholder: "abc123..." },
      { key: "api_key", label: "API Key Wix", placeholder: "IST.xxx...", secret: true },
      { key: "site_id", label: "Site ID (optionnel)", placeholder: "Automatique" },
    ],
    docUrl: "https://dev.wix.com/docs/rest/getting-started/api-keys",
  },
];

const categories = ["Tous", "Banque", "Comptabilité", "CRM", "Email", "Réseaux sociaux", "Intelligence", "Administration", "Base de données"];

/* ─── Connection Card ─── */
const ConnectionCard = ({
  service,
  connection,
  onConnect,
  onDisconnect,
  onTest,
}: {
  service: ServiceDef;
  connection: any;
  onConnect: (id: string, config: Record<string, string>) => void;
  onDisconnect: (id: string) => void;
  onTest: (id: string) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);

  const isConnected = connection?.status === "connected";
  const isAutoConnected = service.id === "grafton";

  const handleConnect = async () => {
    if (service.fields.length > 0) {
      const requiredFilled = service.fields.every((f) => {
        if (f.key === "access_level" || f.placeholder.startsWith("Automatique") || f.placeholder.startsWith("optionnel")) return true;
        return fields[f.key]?.trim();
      });
      if (!requiredFilled) {
        toast.error("Veuillez remplir tous les champs requis");
        return;
      }
    }
    setConnecting(true);
    await onConnect(service.id, fields);
    setConnecting(false);
    setExpanded(false);
  };

  const handleTest = async () => {
    setTesting(true);
    await onTest(service.id);
    setTesting(false);
  };

  return (
    <Card
      className={`transition-all duration-200 hover:shadow-md ${
        isConnected || isAutoConnected
          ? "border-emerald-500/30 bg-emerald-500/[0.02]"
          : "hover:border-primary/20"
      }`}
      data-testid={`connection-card-${service.id}`}
    >
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className={`h-12 w-12 rounded-xl flex items-center justify-center border ${service.color}`}>
            <service.icon className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold">{service.name}</h3>
              {(isConnected || isAutoConnected) ? (
                <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px]" variant="outline">
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Connecté
                </Badge>
              ) : (
                <Badge className="bg-muted text-muted-foreground text-[10px]" variant="outline">
                  <XCircle className="h-3 w-3 mr-1" /> Non connecté
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{service.description}</p>
            <div className="flex flex-wrap gap-1 mt-2">
              {service.feeds.map((f) => (
                <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-primary/5 text-primary/80 border border-primary/10">
                  {f}
                </span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {(isConnected || isAutoConnected) ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-xs"
                  onClick={handleTest}
                  disabled={testing}
                  data-testid={`test-btn-${service.id}`}
                >
                  {testing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  Tester
                </Button>
                {!isAutoConnected && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-xs text-destructive hover:text-destructive"
                    onClick={() => onDisconnect(service.id)}
                    data-testid={`disconnect-btn-${service.id}`}
                  >
                    <XCircle className="h-3 w-3" /> Déconnecter
                  </Button>
                )}
              </>
            ) : (
              <Button
                size="sm"
                variant="hero"
                className="gap-1.5 text-xs"
                onClick={() => setExpanded(!expanded)}
                data-testid={`connect-btn-${service.id}`}
              >
                <Plug className="h-3 w-3" /> Connecter
                {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </Button>
            )}
          </div>
        </div>

        {/* Expanded setup panel */}
        {expanded && !isConnected && !isAutoConnected && (
          <div className="mt-5 pt-5 border-t border-border/50 space-y-4 animate-in slide-in-from-top-2">
            {/* Steps */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-primary" /> Guide de connexion
              </h4>
              <div className="space-y-2">
                {service.setupSteps.map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="h-5 w-5 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <p className="text-xs text-muted-foreground">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Fields */}
            {service.fields.length > 0 && (
              <div className="space-y-3">
                {service.fields.map((field) => (
                  <div key={field.key}>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">{field.label}</label>
                    <div className="relative">
                      <Input
                        data-testid={`field-${service.id}-${field.key}`}
                        type={field.secret && !showSecrets[field.key] ? "password" : "text"}
                        placeholder={field.placeholder}
                        value={fields[field.key] || ""}
                        onChange={(e) => setFields((p) => ({ ...p, [field.key]: e.target.value }))}
                        className="text-sm pr-10"
                      />
                      {field.secret && (
                        <button
                          type="button"
                          onClick={() => setShowSecrets((p) => ({ ...p, [field.key]: !p[field.key] }))}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showSecrets[field.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {service.docUrl && (
                  <a
                    href={service.docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" /> Documentation
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setExpanded(false)} className="text-xs">
                  Annuler
                </Button>
                <Button
                  size="sm"
                  variant="hero"
                  onClick={handleConnect}
                  disabled={connecting}
                  className="gap-1.5 text-xs"
                  data-testid={`confirm-connect-${service.id}`}
                >
                  {connecting ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                  {connecting ? "Connexion..." : "Valider la connexion"}
                </Button>
              </div>
            </div>

            {/* Security note */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/30 border border-border/40">
              <Shield className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Vos clés sont chiffrées et stockées de manière sécurisée. Grafton IA n'a jamais accès à vos identifiants.
                Vous pouvez révoquer l'accès à tout moment.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ─── Page ─── */
const ConnexionsApiPage = () => {
  const [connections, setConnections] = useState<Record<string, any>>({});
  const [activeCategory, setActiveCategory] = useState("Tous");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const res = await fetch(`${API_URL}/api/connections`, { credentials: "include" as RequestCredentials });
      if (res.ok) {
        const data = await res.json();
        const map: Record<string, any> = {};
        for (const c of data.connections || []) map[c.service_id] = c;
        // Grafton is always connected
        map["grafton"] = { service_id: "grafton", status: "connected" };
        setConnections(map);
      }
    } catch {
      // fallback
    }
    setLoading(false);
  };

  const handleConnect = async (serviceId: string, config: Record<string, string>) => {
    try {
      const res = await fetch(`${API_URL}/api/connections/${serviceId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include" as RequestCredentials,
        body: JSON.stringify({ service_id: serviceId, status: "connected", config }),
      });
      if (res.ok) {
        const data = await res.json();
        setConnections((prev) => ({ ...prev, [serviceId]: { ...data, service_id: serviceId } }));
        toast.success(`${SERVICES.find((s) => s.id === serviceId)?.name} connecté avec succès`);
      } else {
        toast.error("Erreur de connexion");
      }
    } catch {
      toast.error("Erreur réseau");
    }
  };

  const handleDisconnect = async (serviceId: string) => {
    try {
      await fetch(`${API_URL}/api/connections/${serviceId}`, {
        method: "DELETE",
        credentials: "include" as RequestCredentials,
      });
      setConnections((prev) => {
        const next = { ...prev };
        delete next[serviceId];
        return next;
      });
      toast.success("Service déconnecté");
    } catch {
      toast.error("Erreur");
    }
  };

  const handleTest = async (serviceId: string) => {
    if (serviceId === "grafton") {
      toast.success("Grafton IA : connexion opérationnelle");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/connections/${serviceId}/test`, {
        method: "POST",
        credentials: "include" as RequestCredentials,
      });
      if (res.ok) {
        toast.success("Connexion opérationnelle");
      } else {
        toast.error("Connexion échouée");
      }
    } catch {
      toast.error("Erreur de test");
    }
  };

  const connectedCount = Object.values(connections).filter((c: any) => c.status === "connected").length;
  const totalCount = SERVICES.length;

  const filteredServices = activeCategory === "Tous"
    ? SERVICES
    : SERVICES.filter((s) => s.category === activeCategory);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <DashboardSidebar />
        <main className="flex-1 overflow-y-auto">
          {/* Hero Header */}
          <div className="relative overflow-hidden border-b border-border bg-gradient-to-br from-primary/5 via-background to-accent/10">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.08),transparent_60%)]" />
            <div className="relative px-6 py-8 md:px-10 md:py-10">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Link2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h1 data-testid="connexions-title" className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
                      Connexions API
                    </h1>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Connectez votre écosystème en quelques clics — plug & play
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-testid="connection-stats">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-primary">{connectedCount}<span className="text-muted-foreground text-sm font-normal">/{totalCount}</span></p>
                    <p className="text-xs text-muted-foreground">services connectés</p>
                  </div>
                  <div className="h-12 w-12 rounded-full border-2 border-primary relative flex items-center justify-center">
                    <svg className="absolute inset-0" viewBox="0 0 48 48">
                      <circle cx="24" cy="24" r="20" fill="none" stroke="hsl(var(--border))" strokeWidth="3" />
                      <circle
                        cx="24" cy="24" r="20" fill="none" stroke="hsl(var(--primary))" strokeWidth="3"
                        strokeDasharray={`${(connectedCount / totalCount) * 125.6} 125.6`}
                        strokeLinecap="round"
                        transform="rotate(-90 24 24)"
                        className="transition-all duration-700"
                      />
                    </svg>
                    <span className="text-[10px] font-bold text-primary z-10">
                      {Math.round((connectedCount / totalCount) * 100)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick stats */}
              <div className="flex gap-4 md:gap-6 mt-6 flex-wrap">
                {[
                  { value: connectedCount, label: "Connectés", color: "bg-emerald-500/10 text-emerald-600" },
                  { value: totalCount - connectedCount, label: "En attente", color: "bg-amber-500/10 text-amber-600" },
                  {
                    value: SERVICES.filter((s) => connections[s.id]?.status === "connected").reduce((sum, s) => sum + s.feeds.length, 0),
                    label: "KPIs alimentés",
                    color: "bg-primary/10 text-primary",
                  },
                ].map((s) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg ${s.color} flex items-center justify-center text-xs font-bold`}>
                      {s.value}
                    </div>
                    <span className="text-sm text-muted-foreground">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="px-6 md:px-10 py-6 space-y-6">
            {/* Info banner */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/15">
              <AlertCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Stratégie Plug & Play</p>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Chaque connexion alimente automatiquement vos KPIs en temps réel. Plus vous connectez de services,
                  plus Grafton devient intelligent et autonome dans le pilotage de votre entreprise.
                </p>
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2" data-testid="connection-filters">
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={activeCategory === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveCategory(cat)}
                  className="rounded-full text-xs"
                  data-testid={`filter-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {cat}
                  {cat !== "Tous" && (
                    <span className="ml-1.5 text-[10px] opacity-60">
                      {SERVICES.filter((s) => s.category === cat).filter((s) => connections[s.id]?.status === "connected").length}/
                      {SERVICES.filter((s) => s.category === cat).length}
                    </span>
                  )}
                </Button>
              ))}
            </div>

            {/* Service Cards */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="connections-grid">
                {filteredServices.map((service) => (
                  <ConnectionCard
                    key={service.id}
                    service={service}
                    connection={connections[service.id]}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                    onTest={handleTest}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
        <GraftonChat
          context={{ page: "connexions-api", connections: Object.keys(connections) }}
          subtitle="Expert intégrations"
          welcomeMessage="Je peux vous guider pour connecter chacun de vos services. Quel outil souhaitez-vous connecter en premier ?"
          quickPrompts={[
            "Comment connecter Qonto ?",
            "Aide-moi avec Pennylane",
            "Configurer Gmail",
            "Connecter mon CRM Axonaut",
          ]}
        />
      </div>
    </SidebarProvider>
  );
};

export default ConnexionsApiPage;
